import React from "react";
import { Select, Input, InputNumber, Button } from "antd";
import { DeleteOutlined, PlusOutlined } from "@ant-design/icons";
import { formatCurrencyWithConversion } from "../../../utils/currencyUtils";

// Helper function to render hierarchical account options
const renderAccountOptions = (accounts) => {
  if (!accounts || !accounts.length) return null;

  // Recursively render account options
  const getLabel = (acc) => (acc.code ? `${acc.code} - ${acc.name}` : acc.name);
  const indentFor = (level) =>
    level > 0 ? `${"\u00A0\u00A0".repeat(level)}• ` : "";

  // Ant Design Select doesn't support nested OptGroup reliably.
  // Keep only top-level groups as OptGroup; deeper levels become indented Options.
  const renderOptions = (items, level = 0) => {
    return items.flatMap((account) => {
      const hasChildren =
        Array.isArray(account.children) && account.children.length > 0;
      const label = getLabel(account);
      const indent = indentFor(level);

      if (hasChildren) {
        if (level === 0) {
          // Top-level group
          return (
            <Select.OptGroup key={`grp-${account.id}`} label={label}>
              {renderOptions(account.children, level + 1)}
            </Select.OptGroup>
          );
        }

        // Deeper level: render a disabled header option, then its children
        return [
          <Select.Option
            key={`hdr-${account.id}`}
            value={`hdr-${account.id}`}
            disabled
          >
            {`${indent}${label}`}
          </Select.Option>,
          ...renderOptions(account.children, level + 1),
        ];
      }

      // Leaf node: selectable option
      return (
        <Select.Option key={account.id} value={account.id}>
          {`${indent}${label}`}
        </Select.Option>
      );
    });
  };

  try {
    return renderOptions(accounts);
  } catch (error) {
    console.error("Error rendering account options:", error);
    return null;
  }
};

const { Option } = Select;

const getDecimalPrecision = () => {
  try { const s = localStorage.getItem("global_decimal_precision"); return s ? parseInt(s, 10) : 3; } catch { return 3; }
};

/**
 * Creates invoice item columns configuration for the invoice items table
 * All fields now use direct state updates to bypass buggy handleItemChange utility
 */
const createCreditItemColumns = (
  products,
  categories,
  accounts,
  offers,
  handleProductChange,
  handleItemChange,
  getCurrentCurrency,
  setInvoiceItems,
  calculateInvoiceTotals,
  removeItem,
  currency = "$",
  navigate = null,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  paymentTerms = [],
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  getIssueDate = null
) => [
  {
    title: (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <span>Category</span>
        <Button
          type="primary"
          size="small"
          icon={<PlusOutlined />}
          style={{ fontSize: "10px", padding: "0 6px", height: "20px" }}
          onClick={(e) => {
            e.stopPropagation();
            window.location.href = "/accounting/settings";
            localStorage.setItem("accounting_settings_active_tab", "2");
          }}
        >
          Add
        </Button>
      </div>
    ),
    dataIndex: "categoryId",
    key: "categoryId",
    width: 180,
    render: (categoryId, record) => {
      return (
        <Select
          placeholder="Select category"
          style={{ width: "100%" }}
          value={categoryId || undefined}
          allowClear
          showSearch
          optionFilterProp="children"
          filterOption={(input, option) =>
            (option?.children ?? "").toLowerCase().includes(input.toLowerCase())
          }
          onChange={(value) => {
            setInvoiceItems((prevItems) => {
              const updatedItems = prevItems.map((item) =>
                item.key === record.key
                  ? { ...item, categoryId: value || null }
                  : item
              );
              calculateInvoiceTotals(updatedItems);
              return updatedItems;
            });
          }}
        >
          {categories &&
            Array.isArray(categories) &&
            categories.map((category) => (
              <Select.Option key={category.id} value={category.id}>
                {category.name}
              </Select.Option>
            ))}
        </Select>
      );
    },
  },

  {
    title: "Offer Code",
    dataIndex: "offerId",
    key: "offerId",
    width: 200,
    render: (offerId, record) => {
      // Convert to number if it exists, otherwise null
      const normalizedOfferId = offerId ? Number(offerId) : null;

      return (
        <Select
          placeholder="Select offer code"
          style={{ width: "100%" }}
          allowClear
          showSearch
          optionFilterProp="children"
          value={normalizedOfferId}
          onChange={(selectedId) => {
            // Convert to number or null
            const id = selectedId ? Number(selectedId) : null;

            // Find the selected offer object
            const selectedOffer = id
              ? offers?.find((o) => Number(o.id) === id)
              : null;

            // DIRECT STATE UPDATE - Bypass handleItemChange completely
            setInvoiceItems((prevItems) => {
              const updatedItems = prevItems.map((item) => {
                if (item.key === record.key) {
                  const updatedItem = { ...item, offerId: id };

                  if (selectedOffer) {
                    const productVariation =
                      Array.isArray(selectedOffer.product_variations_detail) &&
                      selectedOffer.product_variations_detail.length > 0
                        ? selectedOffer.product_variations_detail[0]
                        : null;

                    // Extract product information
                    const productName =
                      productVariation?.name ||
                      selectedOffer.name ||
                      selectedOffer.product_name ||
                      "";
                    const variationDesc =
                      productVariation?.description ||
                      selectedOffer.product_variation ||
                      selectedOffer.variation ||
                      selectedOffer.description ||
                      "";

                    // Get price from offer with multiple fallbacks
                    const unitPrice =
                      selectedOffer.price ||
                      selectedOffer.unit_price ||
                      (productVariation && productVariation.price) ||
                      selectedOffer.product_price ||
                      selectedOffer.amount ||
                      0;

                    // Extract product ID from offer with multiple fallbacks
                    // IMPORTANT: Backend requires a valid product ID
                    const productId =
                      selectedOffer.product_id ||
                      selectedOffer.product ||
                      (productVariation && productVariation.product_id);

                    // Validate that the productId exists within the known products list
                    const isKnownProductId = !!(
                      productId &&
                      Array.isArray(products) &&
                      products.some((p) => Number(p.id) === Number(productId))
                    );

                    // Ensure quantity is at least 1
                    const quantity = updatedItem.quantity || 1;

                    // Calculate subtotal
                    const subtotal = Number(quantity) * Number(unitPrice);

                    // Update the item directly
                    updatedItem.productName = productName;
                    updatedItem.productVariation = variationDesc;
                    updatedItem.unitPrice = unitPrice;
                    updatedItem.subtotal = subtotal;
                    // Only set productId if it matches a known accounting product; otherwise, clear it
                    if (isKnownProductId) {
                      updatedItem.productId = Number(productId);
                    } else {
                      delete updatedItem.productId;
                    }
                  } else {
                    // Clear fields when no offer selected
                    updatedItem.productName = "";
                    updatedItem.productVariation = "";
                    // Don't clear price/subtotal to avoid breaking calculations
                    delete updatedItem.productId;
                  }

                  return updatedItem;
                }
                return item;
              });

              // Calculate totals with updated items
              calculateInvoiceTotals(updatedItems);

              return updatedItems;
            });
          }}
        >
          {Array.isArray(offers) &&
            offers.map((offer) => (
              <Option key={offer.id} value={Number(offer.id)}>
                {offer.code_offer ||
                  offer.code ||
                  offer.name ||
                  `Offer #${offer.id}`}
              </Option>
            ))}
        </Select>
      );
    },
  },

  {
    title: "Product Name",
    dataIndex: "productName",
    key: "productName",
    render: (productName, record) => {
      return (
        <Input
          placeholder="Enter product name"
          value={productName}
          onChange={(e) => {
            // Direct state update bypassing handleItemChange
            setInvoiceItems((prevItems) => {
              const updatedItems = prevItems.map((item) =>
                item.key === record.key
                  ? { ...item, productName: e.target.value }
                  : item
              );
              calculateInvoiceTotals(updatedItems);
              return updatedItems;
            });
          }}
        />
      );
    },
  },

  {
    title: "Product Variation",
    dataIndex: "productVariation",
    key: "productVariation",
    render: (productVariation, record) => {
      return (
        <Input
          placeholder="Enter product variation"
          value={productVariation}
          onChange={(e) => {
            // Direct state update bypassing handleItemChange
            setInvoiceItems((prevItems) => {
              const updatedItems = prevItems.map((item) =>
                item.key === record.key
                  ? { ...item, productVariation: e.target.value }
                  : item
              );
              calculateInvoiceTotals(updatedItems);
              return updatedItems;
            });
          }}
        />
      );
    },
  },

  {
    title: (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <span>Chart of Account</span>
        <Button
          type="primary"
          size="small"
          icon={<PlusOutlined />}
          style={{ fontSize: "10px", padding: "0 6px", height: "20px" }}
          onClick={(e) => {
            e.stopPropagation();
            // Navigate to Advance Accounting with Chart of Accounts tab
            localStorage.setItem(
              "advance_accounting_active_tab",
              "chart-of-accounts"
            );
            if (navigate) {
              navigate("/accounting/advance-accounting");
            } else {
              window.location.href = "/accounting/advance-accounting";
            }
          }}
        >
          Add
        </Button>
      </div>
    ),
    dataIndex: "accountId",
    key: "accountId",
    width: 200,
    render: (accountId, record) => (
      <Select
        placeholder="Select account"
        style={{ width: "100%" }}
        value={accountId}
        allowClear
        dropdownStyle={{ minWidth: 400 }}
        onChange={(value) => {
          // Direct state update bypassing handleItemChange
          setInvoiceItems((prevItems) => {
            const updatedItems = prevItems.map((item) =>
              item.key === record.key ? { ...item, accountId: value } : item
            );
            calculateInvoiceTotals(updatedItems);
            return updatedItems;
          });
        }}
        showSearch
        optionFilterProp="children"
      >
        {accounts && renderAccountOptions(accounts)}
      </Select>
    ),
  },

  {
    title: "Description",
    dataIndex: "description",
    key: "description",
    width: 200,
    render: (description, record) => (
      <Input
        value={description || ""}
        placeholder="Description (required)"
        required
        size="middle"
        onChange={(e) => {
          // Direct state update bypassing handleItemChange
          setInvoiceItems((prevItems) => {
            const updatedItems = prevItems.map((item) =>
              item.key === record.key
                ? { ...item, description: e.target.value }
                : item
            );
            calculateInvoiceTotals(updatedItems);
            return updatedItems;
          });
        }}
        status={!description || description.trim() === "" ? "error" : ""}
      />
    ),
  },

  {
    title: "Quantity",
    dataIndex: "quantity",
    key: "quantity",
    width: 120,
    render: (quantity, record) => (
      <InputNumber
        min={1}
        precision={0}
        value={quantity}
        size="middle"
        style={{ width: "100%" }}
        formatter={(value) => {
          if (!value) return "";
          return `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
        }}
        parser={(value) => {
          if (!value) return "";
          return value.replace(/,/g, "");
        }}
        onChange={(value) => {
          // Direct state update bypassing handleItemChange
          setInvoiceItems((prevItems) => {
            const updatedItems = prevItems.map((item) => {
              if (item.key === record.key) {
                const updatedItem = { ...item, quantity: value };
                // Calculate subtotal when quantity changes
                const unitPrice = Number(updatedItem.unitPrice || 0);
                const quantity = Number(value || 0);
                updatedItem.subtotal = unitPrice * quantity;
                return updatedItem;
              }
              return item;
            });
            calculateInvoiceTotals(updatedItems);
            return updatedItems;
          });
        }}
      />
    ),
  },

  {
    title: "Unit Price",
    dataIndex: "unitPrice",
    key: "unitPrice",
    width: 180,
    render: (unitPrice, record) => {
      // Get currency dynamically - use getCurrentCurrency function if available, otherwise use currency parameter
      const itemCurrency =
        typeof getCurrentCurrency === "function"
          ? getCurrentCurrency()
          : record.currency || currency || "$";
      return (
        <Input
          addonBefore={itemCurrency}
          value={unitPrice}
          placeholder={`0.${"0".repeat(getDecimalPrecision())}`}
          size="middle"
          onPaste={(e) => {
            // Allow paste and process the pasted value
            e.preventDefault();
            const pastedText = (
              e.clipboardData || window.clipboardData
            ).getData("text");
            // Validate pasted value matches number format
            if (pastedText === "" || /^-?\d*\.?\d*$/.test(pastedText)) {
              const inputValue = pastedText;
              setInvoiceItems((prevItems) => {
                const updatedItems = prevItems.map((item) => {
                  if (item.key === record.key) {
                    const updatedItem = { ...item, unitPrice: inputValue };
                    const unitPriceNum = inputValue
                      ? parseFloat(inputValue) || 0
                      : 0;
                    const quantity = Number(updatedItem.quantity || 0);
                    updatedItem.subtotal = unitPriceNum * quantity;
                    return updatedItem;
                  }
                  return item;
                });
                calculateInvoiceTotals(updatedItems);
                return updatedItems;
              });
            }
          }}
          onChange={(e) => {
            const inputValue = e.target.value;
            // Allow empty, numbers, and decimal points while typing
            if (inputValue === "" || /^-?\d*\.?\d*$/.test(inputValue)) {
              setInvoiceItems((prevItems) => {
                const updatedItems = prevItems.map((item) => {
                  if (item.key === record.key) {
                    // Store the raw string value to preserve decimal point while typing
                    const updatedItem = { ...item, unitPrice: inputValue };
                    // Calculate subtotal - parse the value for calculation
                    const unitPriceNum = inputValue
                      ? parseFloat(inputValue) || 0
                      : 0;
                    const quantity = Number(updatedItem.quantity || 0);
                    updatedItem.subtotal = unitPriceNum * quantity;
                    return updatedItem;
                  }
                  return item;
                });
                calculateInvoiceTotals(updatedItems);
                return updatedItems;
              });
            }
          }}
          onBlur={(e) => {
            // On blur, format to 3 decimal places if it's a valid number
            const value = e.target.value;
            if (value && !isNaN(parseFloat(value))) {
              const formattedValue = parseFloat(value).toFixed(getDecimalPrecision());
              setInvoiceItems((prevItems) => {
                const updatedItems = prevItems.map((item) => {
                  if (item.key === record.key) {
                    const updatedItem = { ...item, unitPrice: formattedValue };
                    const unitPriceNum = parseFloat(formattedValue) || 0;
                    const quantity = Number(updatedItem.quantity || 0);
                    updatedItem.subtotal = unitPriceNum * quantity;
                    return updatedItem;
                  }
                  return item;
                });
                calculateInvoiceTotals(updatedItems);
                return updatedItems;
              });
            }
          }}
          style={{ width: "100%", minWidth: 100 }}
        />
      );
    },
  },

  {
    title: "Subtotal",
    dataIndex: "subtotal",
    key: "subtotal",
    width: 150,
    render: (subtotal, record) => {
      // Get currency dynamically - use getCurrentCurrency function if available, otherwise use currency parameter
      const itemCurrency =
        typeof getCurrentCurrency === "function"
          ? getCurrentCurrency()
          : record.currency || currency || "$";
      return formatCurrencyWithConversion(subtotal, itemCurrency);
    },
  },

  {
    title: "Actions",
    key: "actions",
    width: 80,
    render: (_, record) => (
      <Button
        type="text"
        danger
        icon={<DeleteOutlined />}
        onClick={() => removeItem(record.key)}
      />
    ),
  },
];

export default createCreditItemColumns;
